import { useState, useRef, useMemo, useCallback } from 'react';
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2, XCircle, FileJson, Eraser, Copy, Wand2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/utils';

// Create Ajv instance once at module level
const ajv = new Ajv({ allErrors: true, verbose: true });
addFormats(ajv);

interface SchemaValidatorProps {
  schema: object;
  example: object;
  schemaTitle: string;
  schemaSlug?: string;
}

interface ValidationError {
  path: string;
  message: string;
  keyword: string;
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

export function SchemaValidator({ schema, example, schemaTitle, schemaSlug }: SchemaValidatorProps) {
  const [jsonInput, setJsonInput] = useState('');
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const { toast } = useToast();
  const { trackValidation } = useAnalytics();
  const resultRef = useRef<HTMLDivElement>(null);

  // Cache compiled validator - only recompile when schema changes
  const compiledValidator = useMemo(() => {
    try {
      return ajv.compile(schema);
    } catch (e) {
      console.error('Schema compilation error:', e);
      return null;
    }
  }, [schema]);

  // Reusable validation function
  const performValidation = useCallback((input: string): ValidationResult | null => {
    if (!input.trim()) return null;

    let parsedJson: unknown;
    try {
      parsedJson = JSON.parse(input);
    } catch (e) {
      return {
        valid: false,
        errors: [{
          path: '(root)',
          message: `Invalid JSON syntax: ${e instanceof Error ? e.message : 'Parse error'}`,
          keyword: 'syntax',
        }],
      };
    }

    if (!compiledValidator) {
      return {
        valid: false,
        errors: [{ path: '(root)', message: 'Schema failed to compile', keyword: 'schema' }],
      };
    }

    const valid = compiledValidator(parsedJson);
    if (valid) {
      return { valid: true, errors: [] };
    } else {
      return {
        valid: false,
        errors: (compiledValidator.errors || []).map((err) => ({
          path: err.instancePath || '(root)',
          message: err.message || 'Validation error',
          keyword: err.keyword,
        })),
      };
    }
  }, [compiledValidator]);

  // Scroll to result with highlight animation
  const scrollToResult = useCallback(() => {
    setIsHighlighted(true);
    setTimeout(() => setIsHighlighted(false), 1500);
    
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }, 100);
  }, []);

  const validateJson = () => {
    if (!jsonInput.trim()) {
      toast({
        title: 'No JSON provided',
        description: 'Please paste JSON data to validate.',
        variant: 'destructive',
      });
      return;
    }

    const result = performValidation(jsonInput);
    setValidationResult(result);
    scrollToResult();

    // Track validation
    if (result && schemaSlug) {
      trackValidation(schemaSlug, result.valid);
    }
  };

  const loadExample = () => {
    const exampleJson = JSON.stringify(example, null, 2);
    setJsonInput(exampleJson);
    
    // Auto-validate the example
    const result = performValidation(exampleJson);
    setValidationResult(result);
    
    toast({ title: 'Example loaded & validated' });
    scrollToResult();
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      setJsonInput(JSON.stringify(parsed, null, 2));
      toast({ title: 'JSON formatted' });
    } catch {
      toast({
        title: 'Cannot format',
        description: 'Invalid JSON syntax',
        variant: 'destructive',
      });
    }
  };

  const clearInput = () => {
    setJsonInput('');
    setValidationResult(null);
    setIsHighlighted(false);
  };

  const copyReport = () => {
    if (!validationResult) return;
    
    let report = `Validation Report for ${schemaTitle}\n`;
    report += `${'='.repeat(40)}\n\n`;
    
    if (validationResult.valid) {
      report += '✅ VALID - Data conforms to the schema.\n';
    } else {
      report += `❌ INVALID - ${validationResult.errors.length} error(s) found:\n\n`;
      validationResult.errors.forEach((err, i) => {
        report += `${i + 1}. Path: ${err.path}\n`;
        report += `   Error: ${err.message}\n`;
        report += `   Type: ${err.keyword}\n\n`;
      });
    }
    
    navigator.clipboard.writeText(report);
    toast({ title: 'Report copied to clipboard' });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs sm:text-sm text-muted-foreground">
          Paste your JSON data below to validate it against the <strong>{schemaTitle}</strong> schema.
        </p>
      </div>

      <Textarea
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        placeholder='{"id": "...", "firstName": "...", ...}'
        className="font-mono text-xs sm:text-sm min-h-[180px] md:min-h-[250px] resize-y"
      />

      <div className="flex flex-wrap gap-2">
        <Button onClick={loadExample} variant="outline" size="sm">
          <FileJson className="h-4 w-4" />
          <span className="hidden sm:inline ml-2">Load Example</span>
        </Button>
        <Button onClick={formatJson} variant="outline" size="sm" disabled={!jsonInput}>
          <Wand2 className="h-4 w-4" />
          <span className="hidden sm:inline ml-2">Format</span>
        </Button>
        <Button onClick={clearInput} variant="outline" size="sm" disabled={!jsonInput}>
          <Eraser className="h-4 w-4" />
          <span className="hidden sm:inline ml-2">Clear</span>
        </Button>
        <div className="flex-1 min-w-0" />
        <Button onClick={validateJson} disabled={!jsonInput} className="shrink-0">
          <span className="hidden sm:inline">Validate JSON</span>
          <span className="sm:hidden">Validate</span>
        </Button>
      </div>

      <div 
        ref={resultRef}
        className={cn(
          "transition-all duration-300 rounded-lg",
          isHighlighted && "ring-2 ring-primary ring-offset-2 ring-offset-background"
        )}
      >
        {validationResult && (
          <div className="space-y-3 animate-fade-in">
            {validationResult.valid ? (
              <Alert className="border-green-500/50 bg-green-500/10">
                <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                <AlertTitle className="text-green-600">Valid!</AlertTitle>
                <AlertDescription className="text-green-600/80 text-sm">
                  Your data conforms to the <strong>{schemaTitle}</strong> schema (v2.0.0).
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <XCircle className="h-5 w-5 shrink-0" />
                <AlertTitle>{validationResult.errors.length} Validation Error{validationResult.errors.length !== 1 ? 's' : ''}</AlertTitle>
                <AlertDescription>
                  <ul className="mt-2 space-y-2 text-xs sm:text-sm">
                    {validationResult.errors.map((err, i) => (
                      <li key={i} className="font-mono bg-destructive/10 rounded p-2 break-words">
                        <span className="font-semibold break-all">{err.path}:</span> {err.message}
                        <span className="text-xs ml-1 sm:ml-2 opacity-70">({err.keyword})</span>
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <Button variant="ghost" size="sm" onClick={copyReport}>
              <Copy className="h-4 w-4" />
              <span className="ml-2">Copy Report</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
